// @ts-nocheck
import { createApp, watch } from 'vue'
import App from './App.vue'
import { routes, setRouter } from './router'
import store from './store'
import i18n from './i18n'
import { initResponsiveFontSize } from './utils/rem/rem'
import { getUserToken } from './utils/user'
import { applyLocaleDir } from './utils/language'
import { initCrossTabAuthSync } from './utils/authSync'
import { routeSeoMap, defaultSeoMeta, SITE_URL, DEFAULT_OG_IMAGE, canonicalFor, setCanonical, setBreadcrumbJsonLd, truncateForMeta, resolveLocalizedSeoMeta, updateDynamicSeo } from './config/seo'
import type { RouteSeoMeta } from './config/seo'

import './assets/styles/common.scss'
import './assets/styles/style.scss'
import './assets/styles/element-theme.scss'

import nicon from './components/common/nicon/index.vue'
import nline from './components/common/nline/index.vue'
import nbutton from './components/common/nbutton/index.vue'
import closeButton from './components/common/close-button/index.vue'
import iconButton from './components/common/icon-button/index.vue'
import ndialog from './components/common/ndialog/index.vue'
import ntab from './components/common/ntab/index.vue'
import nskeleton from './components/common/nskeleton/index.vue'


import router, { registerIframeRoutes } from './router'
const app = createApp(App)

// 最先同步加载本地菜单缓存并注册 iframe 动态路由，确保首屏路由解析与冷启动能精准匹配
store.dispatch('menu/loadLocal');
const localIframeMenus = store.getters['menu/iframeMenus'];
if (localIframeMenus.length > 0) {
    registerIframeRoutes(router, localIframeMenus);
}

// SEO meta tags proxy
let isInitialLoad = true
const getRouteSeoMeta = (to) => {
    const routeName = (to.name) || ''
    const routeSeo = routeSeoMap[routeName as string] || (to.meta?.seo as RouteSeoMeta) || defaultSeoMeta
    return resolveLocalizedSeoMeta(routeSeo, i18n.global.locale.value as string)
}
router.beforeEach((to, from, next) => {
    const seo = getRouteSeoMeta(to)

    // Canonical + og:url are path-derived and always correct, so set them on
    // EVERY navigation (including the first) — otherwise an SPA route change
    // leaves the previous page's canonical (often the homepage) in place.
    setCanonical(canonicalFor(to.path))

    // Per-route BreadcrumbList (Home → functional page) — set on every navigation
    // including direct landings, so a crawler rendering /chat etc. sees the site
    // directory path. Routes without a `breadcrumb` label clear any prior one.
    setBreadcrumbJsonLd(seo.breadcrumb, to.path)

    const keywordsMeta = document.querySelector('meta[name="keywords"]')
    if (keywordsMeta) keywordsMeta.setAttribute('content', seo.keywords || '')

    if (!isInitialLoad) {
        if (seo.title) document.title = seo.title
        const desc = truncateForMeta(seo.description)
        const descMeta = document.querySelector('meta[name="description"]')
        if (descMeta && desc) descMeta.setAttribute('content', desc)

        const ogTitle = document.querySelector('meta[property="og:title"]')
        if (ogTitle && (seo.ogTitle || seo.title)) ogTitle.setAttribute('content', seo.ogTitle || seo.title)

        const ogDesc = document.querySelector('meta[property="og:description"]')
        if (ogDesc && (seo.ogDescription || desc)) ogDesc.setAttribute('content', (seo.ogDescription || desc) as string)

        const ogImage = document.querySelector('meta[property="og:image"]')
        if (ogImage && seo.ogImage) ogImage.setAttribute('content', seo.ogImage)

        const ogImageWidth = document.querySelector('meta[property="og:image:width"]')
        if (ogImageWidth && seo.ogImage) ogImageWidth.setAttribute('content', "1200")

        const ogImageHeight = document.querySelector('meta[property="og:image:height"]')
        if (ogImageHeight && seo.ogImage) ogImageHeight.setAttribute('content', "630")

    }
    isInitialLoad = false
    next()
})

app.use(router)
app.use(store)
app.use(i18n)

// Apply the active locale's writing direction (rtl for ar) + <html lang> at boot.
applyLocaleDir(i18n.global.locale.value as string)

watch(
    () => i18n.global.locale.value,
    () => {
        updateDynamicSeo(getRouteSeoMeta(router.currentRoute.value))
    }
)

app.component('nicon', nicon)
app.component('nline', nline)
app.component('nbutton', nbutton)
app.component('close-button', closeButton)
app.component('icon-button', iconButton)
app.component('ndialog', ndialog)
app.component('ntab', ntab)
app.component('nskeleton', nskeleton)

initResponsiveFontSize()

// 跨标签页登录态同步：其它 tab（含子站）登出删除 USER_TOKEN 时，主站同步切回未登录态
initCrossTabAuthSync()

router.beforeEach(async (to, from, next) => {
    const requireAuth = to.meta?.requireAuth
    const token = getUserToken()
    if (token) {
        if (to.name === 'home') {
            next({ name: 'chat', replace: true })
            return
        }
    } else {
        if (requireAuth) {
            next({ name: 'home', replace: true })
            return
        }
    }
    next()
})

// --- Self-heal stale-deploy chunk failures --------------------------------
// After a deploy, content-hashed asset chunks are rotated and the old hashes
// are purged. A browser holding pre-deploy HTML — an edge-cached page, or a
// long-open tab — then requests a chunk that no longer exists → 404 → the lazy
// view never mounts → blank content (observed on /blog after refresh). Recover
// by forcing ONE full reload, which pulls the current HTML + matching asset
// graph. The sessionStorage guard prevents a reload loop if the freshly-loaded
// HTML still can't find the chunk (e.g. an edge cache is serving stale HTML).
const CHUNK_RELOAD_KEY = 'sovi:chunk-reloaded'
function reloadOnceForStaleChunks() {
    if (sessionStorage.getItem(CHUNK_RELOAD_KEY)) return
    sessionStorage.setItem(CHUNK_RELOAD_KEY, '1')
    window.location.reload()
}
// Vite fires this when a preloaded / dynamically-imported chunk fails to load.
window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    reloadOnceForStaleChunks()
})
// vue-router surfaces the same failure as a navigation error on lazy routes.
router.onError((error) => {
    if (/dynamically imported module|Importing a module script failed|Failed to fetch/i.test(String(error && error.message))) {
        reloadOnceForStaleChunks()
    }
})

router.afterEach(() => {
    window.scrollTo(0, 0)
    // A navigation completed → re-arm the guard so a future stale deploy in
    // this same tab can self-heal again.
    sessionStorage.removeItem(CHUNK_RELOAD_KEY)
})

app.mount('#app')
